import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import { Line } from "react-chartjs-2";
import dayjs from "dayjs";
import { cachedCall } from "../../hooks/useCachedResource";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Title, Tooltip, Legend);

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const FILTERS = [
  { value: "today", label: "Today" },
  { value: "last7days", label: "Week Wise" },
  { value: "last30days", label: "Month Wise" },
  { value: "last12months", label: "Year Wise" },
  { value: "custom", label: "Custom" },
];

const generateDailyBuckets = (start, end) => {
  const buckets = [];
  let current = dayjs(start);
  const endDate = dayjs(end);
  while (current.isBefore(endDate) || current.isSame(endDate, "day")) {
    buckets.push({
      dateStr: current.format("YYYY-MM-DD"),
      label: current.format("DD MMM"),
      value: 0
    });
    current = current.add(1, "day");
  }
  return buckets;
};

const generateMonthlyBuckets = (start, end) => {
  const buckets = [];
  let current = dayjs(start).startOf("month");
  const endDate = dayjs(end).startOf("month");
  while (current.isBefore(endDate) || current.isSame(endDate, "month")) {
    buckets.push({
      dateStr: current.format("YYYY-MM"),
      label: current.format("MMM YYYY"),
      value: 0
    });
    current = current.add(1, "month");
  }
  return buckets;
};

/**
 * Chart data is cached longer than the counters.
 *
 * A day-bucketed signup chart barely moves within a few minutes, and it is the
 * most expensive query on the page — a grouped aggregate over the users table.
 */
const CHART_TTL_MS = 300_000;

export default function UserChartPage() {
  const [filterType, setFilterType] = useState("last7days");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);

  const totalInRange = useMemo(
    () => chartData?.datasets?.[0]?.data?.reduce((sum, v) => sum + v, 0) ?? 0,
    [chartData]
  );

  const fetchChartData = async () => {
    setLoading(true);
    try {
      let queryStart = startDate;
      let queryEnd = endDate;
      const today = dayjs();

      if (filterType === "today") {
        queryStart = today.format("YYYY-MM-DD");
        queryEnd = today.format("YYYY-MM-DD");
      } else if (filterType === "last7days") {
        queryStart = today.subtract(6, "day").format("YYYY-MM-DD");
        queryEnd = today.format("YYYY-MM-DD");
      } else if (filterType === "last30days") {
        queryStart = today.subtract(29, "day").format("YYYY-MM-DD");
        queryEnd = today.format("YYYY-MM-DD");
      } else if (filterType === "last12months") {
        queryStart = today.subtract(11, "month").startOf("month").format("YYYY-MM-DD");
        queryEnd = today.endOf("month").format("YYYY-MM-DD");
      }

      if (filterType === "custom" && (!queryStart || !queryEnd)) {
        setLoading(false);
        return;
      }

      const token = localStorage.getItem("token");
      
      const apiStartDate = queryStart ? dayjs(queryStart).startOf("day").toISOString() : null;
      const apiEndDate = queryEnd ? dayjs(queryEnd).endOf("day").toISOString() : null;

      // Keyed by the exact request, so flipping between filters and coming
      // back to the dashboard reuses what was already fetched instead of
      // asking the server to aggregate the same range again.
      const data = await cachedCall(
        `user-chart:${filterType}:${apiStartDate}:${apiEndDate}`,
        async () => {
          const res = await axios.post(
            `${baseUrl}/auth/dashboard/user-chart`,
            { filterType, startDate: apiStartDate, endDate: apiEndDate },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          return res.data;
        },
        CHART_TTL_MS
      );

      if (data?.status) {
        let buckets = [];
        if (filterType === "last12months") {
          buckets = generateMonthlyBuckets(queryStart, queryEnd);
        } else {
          buckets = generateDailyBuckets(queryStart, queryEnd);
        }

        const apiData = data.data || [];
        const apiMap = new Map();
        
        apiData.forEach(item => {
           if (!item.date) return;
           const itemDate = dayjs(item.date); 
           const key = filterType === "last12months" 
             ? itemDate.format("YYYY-MM") 
             : itemDate.format("YYYY-MM-DD");
           apiMap.set(key, (apiMap.get(key) || 0) + (item.totalUser || 0));
        });

        const labels = buckets.map(b => b.label);
        const values = buckets.map(b => apiMap.get(b.dateStr) || 0);
        // A crowded axis reads worse than a clean curve — dots only show up
        // once there's little enough data for each one to be meaningful.
        const showPoints = values.length <= 31;

        setChartData({
          labels,
          datasets: [
            {
              label: "New Users",
              data: values,
              borderColor: "#4F46E5",
              backgroundColor: (context) => {
                const { ctx, chartArea } = context.chart;
                if (!chartArea) return "rgba(79,70,229,0.15)";
                const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                gradient.addColorStop(0, "rgba(79,70,229,0.28)");
                gradient.addColorStop(1, "rgba(79,70,229,0.01)");
                return gradient;
              },
              borderWidth: 2.5,
              pointRadius: showPoints ? 3 : 0,
              pointHoverRadius: 5,
              pointBackgroundColor: "#4F46E5",
              pointBorderColor: "#fff",
              pointBorderWidth: 1.5,
              tension: 0.4,
              fill: true,
            },
          ],
        });
      }
    } catch (error) {
      console.error("Error fetching chart data:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchChartData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, startDate, endDate]);

  return (
    <div className="bg-white rounded-xl shadow-sm p-5 sm:p-6">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div>
          <h2 className="text-base font-semibold text-gray-800">User Growth</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            New registrations over time
            {chartData && (
              <span className="ml-2 text-gray-300">
                · <span className="font-semibold text-indigo-600">{totalInRange.toLocaleString()}</span> in
                range
              </span>
            )}
          </p>
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilterType(f.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                filterType === f.value
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Custom date range */}
      {filterType === "custom" && (
        <div className="flex flex-wrap gap-3 mb-5 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-400">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-400">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={fetchChartData}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-indigo-700 transition"
            >
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Chart area — a fixed height keeps this from stretching or
          collapsing inside the dashboard's flex layout the way a chart
          without maintainAspectRatio:false does. */}
      <div className="relative h-64 sm:h-72">
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <div className="w-8 h-8 border-4 border-indigo-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : chartData && totalInRange > 0 ? (
          <Line
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              interaction: { mode: "index", intersect: false },
              plugins: {
                legend: { display: false },
                tooltip: {
                  intersect: false,
                  mode: "index",
                  backgroundColor: "#111827",
                  padding: 10,
                  cornerRadius: 8,
                  displayColors: false,
                  titleFont: { size: 12, weight: "600" },
                  bodyFont: { size: 12 },
                  callbacks: {
                    label: (item) => `${item.formattedValue} new user${item.raw === 1 ? "" : "s"}`,
                  },
                },
              },
              scales: {
                x: {
                  grid: { display: false },
                  ticks: { font: { size: 11 }, autoSkip: true, maxRotation: 0 },
                },
                y: {
                  beginAtZero: true,
                  grid: { color: "rgba(0,0,0,0.05)" },
                  ticks: { font: { size: 11 }, precision: 0 },
                },
              },
            }}
          />
        ) : (
          <div className="flex flex-col justify-center items-center h-full text-gray-400 text-sm gap-1">
            <span>No new users in this range</span>
          </div>
        )}
      </div>
    </div>
  );
}
